/** Connected yDoc */

import { YjsEditor } from '@tunasong/sync-lib'
import { useEffect } from 'react'
import { Editor } from '../slate-typescript.js'
import type { TunaEditor } from '../plugin-types.js'

/**
 * Origin used for the post-connect shape-corrective normalize. slate-yjs
 * gates broadcast on `isLocalOrigin(origin)` which is `origin === localOrigin`;
 * any value different from the editor's configured `localOrigin` means the
 * resulting Slate ops are applied locally but NOT pushed back into the YDoc.
 * Concrete value doesn't matter — only its identity.
 */
const SILENT_NORMALIZE_ORIGIN = Symbol('tuna/silent-normalize')

/** Connect YDoc to the editor */
export const useYDocConnect = (editor: TunaEditor | null) => {
  // Connect editor in useEffect to comply with concurrent mode requirements.
  useEffect(() => {
    if (!editor) {
      return
    }
    if (YjsEditor.isYjsEditor(editor) === false) {
      throw new Error('useYDocConnected: editor must be a YjsEditor')
    }
    const yjsEditor = editor as unknown as YjsEditor
    YjsEditor.connect(yjsEditor)

    /**
     * GH #1161: a fresh `connect()` populates editor.children from the YDoc,
     * which kicks Slate's normalize pass. Documents persisted in legacy
     * shapes (e.g. `li > text` without `lic`) trigger plate-list's
     * `withNormalizeList` to insert + move ~2 ops per list item — every one
     * of which slate-yjs would otherwise mirror back to the shared YDoc and
     * out over the wire. On a 500-item bullet list that's ~1000 writes
     * stacked onto the network for what is logically a no-op render.
     *
     * Run the shape-correction normalize under a non-localOrigin so the ops
     * land in editor.children but skip the YJS broadcast (slate-yjs gates
     * broadcast on `isLocalOrigin(currentOrigin)`). Each client performs
     * the same correction locally — full convergence — and the YDoc itself
     * is healed by the next legitimate edit.
     */
    YjsEditor.withOrigin(yjsEditor, SILENT_NORMALIZE_ORIGIN, () => {
      Editor.normalize(yjsEditor as unknown as TunaEditor, { force: true })
    })

    return () => YjsEditor.disconnect(yjsEditor)
  }, [editor])
}
